/**
 * NT-TOAST-GLOBAL-01: reine, DOM-freie Logik für den globalen Benachrichtigungs-Toast —
 * ausgelagert aus dem Orchestrator (`global-toast.ts`, Muster crimeOps.orders-filter.ts/
 * dashboard.betriebe-shortcuts.ts), damit Eligibility/Queue/Erstladung/Gelesen-Routing isoliert
 * testbar sind, ohne DOM-/Polling-Code zu laden.
 *
 * Zwei Quellen mit unterschiedlichem Gelesen-Zustand (siehe Schritt-0-Bericht):
 * - GAME:  GameNotification, /api/v1/notifications, `read`-Boolean direkt auf der Zeile.
 * - ADMIN: Notification,     /api/v1/player-notifications, Gelesen-Zustand über NotificationRead
 *          (Broadcasts können `playerId=null` sein, daher kein einfaches Boolean-Feld).
 */

export type ToastSource = 'GAME' | 'ADMIN';

/**
 * N6 — je NotificationType ein Kennzeichen, ob er als Toast erscheint oder nur ins Event-Log/die
 * Glocke geht. Standard für neue/unbekannte Typen: KEIN Toast. Einzige Stelle für diese
 * Zuordnung — nicht verstreut im Frontend duplizieren.
 *
 * GAME: NPC_MESSAGE toastet bewusst nicht (eigenes blockierendes Modal, services/npc-messages.ts).
 * ADMIN: nur die beiden Razzia-Typen — alle anderen bestehenden Admin-Typen (UPDATE/RESTART/
 * GENERAL/SCHEINE_GRANT/SCHEINE_ANNOUNCEMENT/SUGGESTION_STATUS_CHANGE/ALPHA_TESTER_BADGE_GRANTED/
 * STREET_CONTROL_CHANGE/STREET_CHALLENGE_ANNOUNCED/DEALER_CORNER_FREED) bleiben bewusst
 * Glocke-only (Schritt-0-Freigabe: nur die im Prompt explizit genannten Ereignisse anbinden).
 *
 * NT-AUFTRAGSBRETT-04: AUFTRAG_GESCHEITERT (Frist verpasst/Eigenart-Totalverlust) und
 * AUFTRAG_STICHTAG_ERINNERUNG (STAFFEL-Frist läuft bald ab) ergänzt — Schritt-0 Frage 4 bestätigt:
 * ein neuer Meldungstyp wird ausschließlich hier eingehängt (keine weitere Anbindung nötig).
 *
 * NT-ORG-01: alle acht ORG_*-Fan-out-Typen ergänzt (L4) — Org-Ereignisse sind selten und immer
 * wichtig genug für einen Toast (Einladung, Beitritt/Austritt/Rauswurf, Rangänderung — manuell,
 * automatisch ODER Nachfolge —, Auflösungsantrag/Veto/Auflösung selbst).
 */
const TOAST_ELIGIBLE_TYPES: Record<ToastSource, ReadonlySet<string>> = {
  GAME: new Set([
    'NPC_RETURN', 'AUFTRAG_ABGESCHLOSSEN', 'AUFTRAG_GESCHEITERT', 'AUFTRAG_STICHTAG_ERINNERUNG',
    'ORG_EINGELADEN', 'ORG_BEIGETRETEN', 'ORG_AUSGETRETEN', 'ORG_RAUSGEWORFEN',
    'ORG_RANG_GEAENDERT', 'ORG_AUFLOESUNG_BEANTRAGT', 'ORG_AUFLOESUNG_VETO', 'ORG_AUFGELOEST',
    'TALENTPUNKTE_NACHGEZAHLT', // NT-PROGRESSION-01: einmalige Nachzahlung
    // NT-ORG-07a: Org-Aufträge — Start, Abschluss, Abbruch (Fan-out an alle Mitglieder).
    'ORG_AUFTRAG_GESTARTET', 'ORG_AUFTRAG_ABGESCHLOSSEN', 'ORG_AUFTRAG_ABGEBROCHEN',
    // NT-ORG-07b — Produktionsangebote + eigener Org-Job
    'ORG_PRODUKTION_GESTARTET', 'ORG_PRODUKTION_ABGESCHLOSSEN', 'ORG_PRODUKTION_ABGEBROCHEN', 'ORG_JOB_ERLEDIGT',
  ]),
  ADMIN: new Set(['HIDEOUT_RAID_WARNING', 'HIDEOUT_RAID_EXECUTED']),
};

export function isToastEligible(source: ToastSource, type: string): boolean {
  return TOAST_ELIGIBLE_TYPES[source].has(type);
}

export interface ToastCandidate {
  id: string;
  source: ToastSource;
  title: string;
  text: string;
  actionUrl: string | null;
  createdAt: string; // ISO
}

/** Eindeutiger Schlüssel über beide Quellen hinweg (Vorsichtsmaßnahme, keine echte Kollisionsgefahr — cuids sind global eindeutig). */
export function toCandidateKey(candidate: Pick<ToastCandidate, 'source' | 'id'>): string {
  return `${candidate.source}:${candidate.id}`;
}

/**
 * N3 (clientseitiger Teil) — verhindert, dass dieselbe Meldung innerhalb derselben laufenden
 * Session ein zweites Mal getoastet wird, bevor die Gelesen-Markierung serverseitig
 * abgeschlossen ist (z. B. zwei schnell aufeinanderfolgende Poll-Zyklen). Der eigentliche Schutz
 * über Reload/zweiten Tab hinweg kommt serverseitig aus dem jeweiligen Gelesen-Zustand — diese
 * Funktion filtert deshalb NICHT nach isRead (das erledigt der Aufrufer vorher je Quelle), sie
 * schützt nur gegen ein Re-Toasten innerhalb desselben Tabs.
 */
export function selectNewToastCandidates(
  candidates: readonly ToastCandidate[],
  alreadyToastedKeys: ReadonlySet<string>,
): ToastCandidate[] {
  return candidates.filter((c) => !alreadyToastedKeys.has(toCandidateKey(c)));
}

/**
 * N7 — beim allerersten Laden (frisch initialisiertes Modul, z. B. nach Login) höchstens die
 * neueste Meldung zeigen. Während einer geschlossenen Sitzung aufgelaufene Meldungen landen
 * NICHT als Toast-Schwall auf dem Bildschirm, sondern bleiben in der Glocke/im Event-Log.
 */
export function selectInitialLoadToasts(candidates: readonly ToastCandidate[]): ToastCandidate[] {
  if (candidates.length === 0) return [];
  const sorted = [...candidates].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
  const newest = sorted[0];
  return newest ? [newest] : [];
}

/** Ältestes zuerst — Anzeigereihenfolge über beide Quellen hinweg vereinheitlicht. */
export function mergeAndSortCandidates(
  a: readonly ToastCandidate[],
  b: readonly ToastCandidate[],
): ToastCandidate[] {
  return [...a, ...b].sort(
    (x, y) => new Date(x.createdAt).getTime() - new Date(y.createdAt).getTime(),
  );
}

/**
 * N4 — maximal `max` Toasts gleichzeitig sichtbar. Kommen neue dazu und die Anzeige ist bereits
 * voll, rücken die ältesten (am Anfang von `current`) raus, um Platz für die neuesten zu machen.
 */
export function pushToastQueue<T>(current: readonly T[], incoming: readonly T[], max: number): T[] {
  const combined = [...current, ...incoming];
  return combined.length <= max ? combined : combined.slice(combined.length - max);
}

/**
 * N3/Frage 2 (Freigabe) — Gelesen-Markierung läuft für beide Quellen über ihre jeweils
 * BESTEHENDE Route (dieselbe, die auch die Glocke/der Crime-Ops-Tab nutzt) — keine neue Route,
 * keine Divergenz zwischen Toast und Glocke.
 */
export function buildReadEndpoint(source: ToastSource, id: string): string {
  return source === 'GAME'
    ? `/notifications/${encodeURIComponent(id)}/read`
    : `/player-notifications/${encodeURIComponent(id)}/read`;
}

export interface ToastDecision {
  /** Wird tatsächlich getoastet UND serverseitig als gelesen markiert. */
  toShow: ToastCandidate[];
  /** Schlüssel, die ab sofort als "gesehen" gelten (nie wieder getoastet) — Obermenge von toShow. */
  keysToMarkSeen: string[];
}

/**
 * Verbindet N3 (session-Dedup) und N7 (kein Schwall beim Laden) zu EINER Entscheidung —
 * zentral hier statt im DOM-gekoppelten Orchestrator, damit auch das Zusammenspiel beider Regeln
 * über mehrere Poll-Zyklen hinweg isoliert testbar ist (siehe
 * test_kein_toast_schwall_im_naechsten_zyklus_nach_erstladung).
 *
 * Kern der Regel: beim allerersten Laden gelten ALLE zu diesem Zeitpunkt unread/eligible
 * Kandidaten als "gesehen" — nicht nur der eine tatsächlich gezeigte. Sonst würde der Rest im
 * NÄCHSTEN Poll-Zyklus (isFirstLoad dann false) doch noch als Schwall nachtoasten, nur um einen
 * Zyklus verzögert. Der übrige, hier nur als "gesehen" vermerkte Teil bleibt bewusst ungelesen
 * und damit sichtbar in Glocke/Event-Log (keine Markierung in `toShow`).
 */
export function decideToasts(
  merged: readonly ToastCandidate[],
  alreadyToastedKeys: ReadonlySet<string>,
  isFirstLoad: boolean,
): ToastDecision {
  const unseen = selectNewToastCandidates(merged, alreadyToastedKeys);
  const toShow = isFirstLoad ? selectInitialLoadToasts(unseen) : unseen;
  const seenNow = isFirstLoad ? unseen : toShow;
  return { toShow, keysToMarkSeen: seenNow.map(toCandidateKey) };
}
